import { PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { DateTimePicker } from '@components/DateTimePicker'
import { countLabel } from '@/utils/plural'
import Modal from '@components/Modal'
import { useAuth } from '@context/AuthContext'
import { useCreateFlightAuto, useCreateFlightManual } from '@hooks'
import { GetFlightGroupResponse, GetUserType } from '@orbit/api-types'
import { isNotFound } from '@lib/apiError'
import { fullMemberName, sortMembersWithCurrentFirst } from '@lib/members'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import { getDepartureDisplay, getFlightCode, getRouteString } from '../flightHelpers'
import {
	afterLegAdded,
	applyAutoLookupFailure,
	emptyFlightDraft,
	MODE_OPTIONS,
	setLegArrivalTime,
	setLegDepartureTime,
	setLegField,
	startAnotherLeg,
	toAutoCreatePayload,
	toManualCreatePayload,
	togglePassenger,
	validateFlightDraft,
	type FlightDraft,
	type FlightMode,
} from '../flightForm'
import {
	AddAnotherLegButton,
	Button,
	ButtonGroup,
	ErrorText,
	FieldRow,
	FormDescription,
	FormGroup,
	FormInput,
	FormLabel,
	LegSummaryCode,
	LegSummaryRoute,
	LegSummaryRow,
	LegSummaryText,
	LegSummaryTime,
	LegsSummary,
	LegsSummaryTitle,
	ManualFallbackNotice,
	PassengerCheckbox,
	PassengerName,
	PassengerRowStyled,
	PassengersList,
	SummaryBox,
	ModeSwitch,
} from './AddFlightModal.styles'
import { Segmented } from '@components/Segmented'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'
import { TestIds } from '@/testIds'

interface AddFlightModalProps {
	isOpen: boolean
	onClose: () => void
	members: GetUserType[]
	initialFlightGroup?: GetFlightGroupResponse
}


const AddFlightModal: React.FC<AddFlightModalProps> = ({ isOpen, onClose, members, initialFlightGroup }) => {
	const { tripId } = useParams<{ tripId: string }>()
	const { user } = useAuth()
	const createAuto = useCreateFlightAuto()
	const createManual = useCreateFlightManual()

	const [draft, setDraft] = useState<FlightDraft>(() => emptyFlightDraft(members, initialFlightGroup))
	const [flightGroup, setFlightGroup] = useState<GetFlightGroupResponse | null>(null)

	useEffect(() => {
		if (isOpen) {
			setDraft(emptyFlightDraft(members, initialFlightGroup))
			setFlightGroup(initialFlightGroup ?? null)
		}
	}, [isOpen, members, initialFlightGroup])

	const sortedMembers = useMemo(() => sortMembersWithCurrentFirst(members, user?.id), [members, user?.id])

	const setMode = (mode: FlightMode) => setDraft(prev => ({ ...prev, mode }))

	const validation = validateFlightDraft(draft)
	const isSubmitting = createAuto.isPending || createManual.isPending
	const canSubmit = validation.valid && !isSubmitting

	const handleAutoSubmit = async () => {
		if (!tripId || !canSubmit) return
		try {
			const group = await createAuto.mutateAsync({
				tripId,
				data: toAutoCreatePayload(draft, flightGroup?.id),
			})
			setFlightGroup(group)
			setDraft(afterLegAdded)
			toast.success('Flight leg added!')
		} catch (error) {
			if (isNotFound(error)) {
				setDraft(applyAutoLookupFailure)
				toast(
					'Flight not found in lookup. Enter the details manually.',
					{ icon: '✈︎' }
				)
			} else {
				toast.error('Unable to add flight leg. Please try again.')
			}
		}
	}

	const handleManualSubmit = async () => {
		if (!tripId || !canSubmit) return
		try {
			const group = await createManual.mutateAsync({
				tripId,
				data: toManualCreatePayload(draft, flightGroup?.id),
			})
			setFlightGroup(group)
			setDraft(afterLegAdded)
			toast.success('Flight leg added!')
		} catch {
			toast.error('Unable to add flight leg. Please try again.')
		}
	}

	const handleAddAnotherLeg = () => {
		setDraft(startAnotherLeg)
	}

	const handleDone = () => {
		onClose()
	}

	const title = flightGroup
		? `Flight (${countLabel(flightGroup.flights.length, 'leg')})`
		: 'Add Flight'

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			size="large"
			backgroundColor={backgroundOrange}
			closeOnBackdropClick={false}
			footer={
				<ButtonGroup>
					{flightGroup ? (
						<>
							<Button type="button" variant="secondary" onClick={handleDone} data-testid={TestIds.flight.doneButton}>
								Done
							</Button>
							<Button
								type="button"
								variant="primary"
								disabled={!canSubmit}
								onClick={draft.mode === 'auto' ? handleAutoSubmit : handleManualSubmit}
								data-testid={TestIds.flight.addLegButton}
							>
								{isSubmitting ? 'Adding…' : 'Add Leg'}
							</Button>
						</>
					) : (
						<>
							<Button type="button" variant="secondary" onClick={onClose}>
								Cancel
							</Button>
							<Button
								type="button"
								variant="primary"
								disabled={!canSubmit}
								onClick={draft.mode === 'auto' ? handleAutoSubmit : handleManualSubmit}
								data-testid={TestIds.flight.addLegButton}
							>
								{isSubmitting ? 'Adding…' : 'Add Flight'}
							</Button>
						</>
					)}
				</ButtonGroup>
			}
		>
			{flightGroup && (
				<LegsSummary>
					<LegsSummaryTitle>Legs added</LegsSummaryTitle>
					{flightGroup.flights.map(leg => {
						const depStr = getDepartureDisplay(leg)
						return (
							<LegSummaryRow key={leg.id}>
								<LegSummaryText>
									<LegSummaryCode>{getFlightCode(leg)}</LegSummaryCode>
									<LegSummaryRoute>{getRouteString(leg)}</LegSummaryRoute>
									{depStr && <LegSummaryTime>Departs {depStr}</LegSummaryTime>}
								</LegSummaryText>
							</LegSummaryRow>
						)
					})}
					<AddAnotherLegButton type="button" onClick={handleAddAnotherLeg}>
						<PlusIcon sx={{ fontSize: iconSizes.sm }} />
						Add Another Leg
					</AddAnotherLegButton>
				</LegsSummary>
			)}

			<ModeSwitch>
				<Segmented aria-label="Flight entry mode" options={MODE_OPTIONS} value={draft.mode} onChange={setMode} />
			</ModeSwitch>

			{draft.manualFallbackActive && draft.mode === 'manual' && (
				<ManualFallbackNotice>
					We couldn't automatically pull this flight. Enter the details manually below.
				</ManualFallbackNotice>
			)}

			{draft.mode === 'auto' ? (
				<>
					<FormGroup>
						<FormLabel htmlFor="flight-code">Flight Code</FormLabel>
						<FormDescription>e.g. AC123, UA2521</FormDescription>
						<FormInput
							id="flight-code"
							data-testid={TestIds.flight.codeField}
							type="text"
							value={draft.flightCode}
							onChange={e => setDraft(prev => ({ ...prev, flightCode: e.target.value.toUpperCase() }))}
							placeholder="AC123"
							maxLength={10}
						/>
					</FormGroup>

					<FormGroup>
						<FormLabel htmlFor="flight-date">Departure Date</FormLabel>
						<DateTimePicker
							id="flight-date"
							testId={TestIds.flight.dateField}
							value={draft.flightDate}
							onChange={next => setDraft(prev => ({ ...prev, flightDate: next }))}
							showTime={false}
							placeholder="Select departure date"
						/>
					</FormGroup>
				</>
			) : (
				<>
					<FieldRow>
						<FormGroup>
							<FormLabel htmlFor="manual-airline">Airline IATA Code</FormLabel>
							<FormInput
								id="manual-airline"
								type="text"
								value={draft.leg.airlineIata}
								onChange={e => setDraft(prev => setLegField(prev, 'airlineIata', e.target.value.toUpperCase()))}
								placeholder="AC"
								maxLength={3}
							/>
						</FormGroup>
						<FormGroup>
							<FormLabel htmlFor="manual-number">Flight Number</FormLabel>
							<FormInput
								id="manual-number"
								type="text"
								value={draft.leg.flightNumber}
								onChange={e => setDraft(prev => setLegField(prev, 'flightNumber', e.target.value))}
								placeholder="123"
								maxLength={6}
							/>
						</FormGroup>
					</FieldRow>

					<FieldRow>
						<FormGroup>
							<FormLabel htmlFor="manual-dep-airport">Departure Airport</FormLabel>
							<FormInput
								id="manual-dep-airport"
								type="text"
								value={draft.leg.departureAirport}
								onChange={e => setDraft(prev => setLegField(prev, 'departureAirport', e.target.value.toUpperCase()))}
								placeholder="YYZ"
								maxLength={3}
							/>
						</FormGroup>
						<FormGroup>
							<FormLabel htmlFor="manual-arr-airport">Arrival Airport</FormLabel>
							<FormInput
								id="manual-arr-airport"
								type="text"
								value={draft.leg.arrivalAirport}
								onChange={e => setDraft(prev => setLegField(prev, 'arrivalAirport', e.target.value.toUpperCase()))}
								placeholder="JFK"
								maxLength={3}
							/>
						</FormGroup>
					</FieldRow>

					<FieldRow>
						<FormGroup>
							<FormLabel htmlFor="manual-dep-time">Departure Time</FormLabel>
							<DateTimePicker
								id="manual-dep-time"
								value={draft.leg.departureTime}
								onChange={next => setDraft(prev => setLegDepartureTime(prev, next))}
								placeholder="Select departure"
								use24HourTime
							/>
						</FormGroup>
						<FormGroup>
							<FormLabel htmlFor="manual-arr-time">Arrival Time (Optional)</FormLabel>
							<DateTimePicker
								id="manual-arr-time"
								value={draft.leg.arrivalTime}
								onChange={next => setDraft(prev => setLegArrivalTime(prev, next))}
								placeholder="Select arrival"
								use24HourTime
							/>
						</FormGroup>
					</FieldRow>
				</>
			)}

			<FormGroup>
				<FormLabel>Passengers</FormLabel>
				<FormDescription>Select who is on this flight leg</FormDescription>
				<PassengersList>
					{sortedMembers.map(member => {
						const isSelected = draft.passengerIds.has(member.id)
						return (
							<PassengerRowStyled
								key={member.id}
								$isSelected={isSelected}
								onClick={() => setDraft(prev => togglePassenger(prev, member.id))}
							>
								<PassengerCheckbox $checked={isSelected} />
								<Avatar user={member} size="md" />
								<PassengerName>{fullMemberName(member, user?.id)}</PassengerName>
							</PassengerRowStyled>
						)
					})}
				</PassengersList>
				<SummaryBox>
					{validation.passengerError ? (
						<ErrorText>{validation.passengerError}</ErrorText>
					) : (
						`${countLabel(draft.passengerIds.size, 'passenger')} selected`
					)}
				</SummaryBox>
			</FormGroup>
		</Modal>
	)
}

export default AddFlightModal
